import { useQuery } from '@tanstack/react-query';
import { fetchCi } from '../api';
import {
  Badge,
  Empty,
  ErrorBox,
  Loading,
  RelativeTime,
  Section,
  Table,
  Value,
} from '../components/ui';

export function CiPage(): JSX.Element {
  const query = useQuery({ queryKey: ['ci'], queryFn: fetchCi, refetchInterval: 120_000 });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { repos, recentFailures } = query.data;
  const withCi = repos.filter((repo) => repo.hasCi);
  const withoutCi = repos.filter((repo) => !repo.hasCi);

  const totalRuns = withCi.reduce((sum, repo) => sum + repo.runs, 0);
  const totalFailed = withCi.reduce((sum, repo) => sum + repo.failed, 0);
  const globalPct = totalRuns === 0 ? null : Math.round((totalFailed / totalRuns) * 1000) / 10;

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Salud de CI/CD</h1>
      <p className="mb-6 text-[12px] text-[var(--color-ink-muted)]">
        {globalPct === null
          ? 'Sin ejecuciones registradas.'
          : `${totalFailed} de ${totalRuns} ejecuciones han fallado (${globalPct} %).`}
      </p>

      <Section title="Por repositorio" subtitle={`${withCi.length} con CI configurada`}>
        <Table
          head={[
            'Repositorio',
            'Proyecto',
            'Workflows',
            'Ejecuciones',
            'Fallidas',
            '% fallo 30 d',
            '% histórico',
            'Fallos 7 d',
            'Última',
          ]}
        >
          {withCi.map((repo) => (
            <tr
              key={repo.repo}
              className="border-b border-[var(--color-border-subtle)] last:border-0"
            >
              <td className="px-3 py-2 font-medium">{repo.repo}</td>
              <td className="px-3 py-2 text-[12px] text-[var(--color-ink-muted)]">
                {repo.projectName ?? (
                  <Value reason="repositorio sin proyecto asignado">{null}</Value>
                )}
              </td>
              <td className="px-3 py-2 tabular-nums">{repo.workflows}</td>
              <td className="px-3 py-2 tabular-nums">{repo.runs}</td>
              <td className="px-3 py-2 tabular-nums">{repo.failed}</td>
              {/* La ventana reciente lleva el color: es la que se cura cuando el CI se
                  arregla. El histórico va al lado, en gris, como contexto. */}
              <td className="px-3 py-2 tabular-nums">
                {repo.failurePct30d === null ? (
                  <Value reason="sin ejecuciones en los últimos 30 días">{null}</Value>
                ) : (
                  <Badge
                    tone={
                      repo.failurePct30d >= 50 ? 'bad' : repo.failurePct30d >= 30 ? 'warn' : 'good'
                    }
                  >
                    {repo.failurePct30d} %
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-[12px] tabular-nums text-[var(--color-ink-muted)]">
                {repo.failurePct === null ? (
                  <Value reason="sin ejecuciones">{null}</Value>
                ) : (
                  `${repo.failurePct} %`
                )}
              </td>
              {/* Solo enrojecen los fallos en ramas que siguen existiendo. Los de una rama
                  borrada se cuentan aparte y en gris: son historia, no trabajo pendiente. */}
              <td className="px-3 py-2 tabular-nums">
                {repo.failedLive7d > 0 ? (
                  <span className="text-red-700 dark:text-red-400">{repo.failedLive7d}</span>
                ) : (
                  0
                )}
                {repo.failed7d > repo.failedLive7d && (
                  <span
                    className="ml-1 text-[11px] text-[var(--color-ink-muted)]"
                    title="Fallos en ramas que ya no existen: no se pueden reproducir ni arreglar"
                  >
                    +{repo.failed7d - repo.failedLive7d} en ramas borradas
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <RelativeTime iso={repo.lastRunAt} />
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="Repositorios sin integración continua"
        subtitle="No es que su CI vaya mal: es que no existe, así que no hay ninguna señal automática"
      >
        {withoutCi.length === 0 ? (
          <Empty>Todos los repositorios tienen CI configurada.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {withoutCi.map((repo) => (
              <li key={repo.repo}>
                <Badge tone="warn">{repo.repo}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Ejecuciones fallidas recientes"
        subtitle="Las de la rama por defecto van marcadas"
      >
        {recentFailures.length === 0 ? (
          <Empty>Sin fallos registrados.</Empty>
        ) : (
          <Table head={['Cuándo', 'Repositorio', 'Workflow', 'Rama', 'Evento', 'Duración', '']}>
            {recentFailures.map((run) => (
              <tr
                key={run.id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="whitespace-nowrap px-3 py-2">
                  <RelativeTime iso={run.startedAt} />
                </td>
                <td className="px-3 py-2 text-[12px]">{run.repo}</td>
                <td className="px-3 py-2">{run.name ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className="font-mono text-[11px]">{run.branch ?? '—'}</span>
                  {run.isDefaultBranch && (
                    <span className="ml-1.5">
                      <Badge tone="bad">rama por defecto</Badge>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-[var(--color-ink-muted)]">{run.event}</td>
                <td className="px-3 py-2 tabular-nums text-[12px]">
                  {run.durationMs === null ? '—' : `${Math.round(run.durationMs / 1000)} s`}
                </td>
                <td className="px-3 py-2">
                  {run.url && (
                    <a
                      href={run.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] underline opacity-70 hover:opacity-100"
                    >
                      GitHub
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}
